import { Check } from "lucide-react";
import { useState } from "react";
import { cn } from "../../../lib/utils";
import { lpConfig } from "../lpConfig";
import { useFadeIn } from "../useFadeIn";

/**
 * 月額料金を表示用にフォーマットする
 * @param {number} price - 月額料金
 * @return {string} カンマ区切りフォーマットの料金文字列
 */
function formatPrice(price: number): string {
  return `\u00A5${price.toLocaleString()}`;
}

/**
 * 料金プランセクション
 * 月額・年額切替トグルと4つの料金プランカードを表示する
 * @return {JSX.Element} 料金セクションコンポーネント
 */
export function LpPricing() {
  const { ref, isVisible } = useFadeIn();
  const [isAnnual, setIsAnnual] = useState(false);

  return (
    <section
      id="pricing"
      className="py-24 lg:py-32 bg-gradient-to-b from-gray-50 to-white"
    >
      <div
        ref={ref}
        className={cn(
          "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 lp-fade-in-up",
          isVisible && "lp-visible",
        )}
      >
        <div className="text-center">
          <p className="text-xs font-bold tracking-widest text-[#1667CF] uppercase mb-4">
            {lpConfig.pricing.sectionLabel}
          </p>
          <h2 className="text-4xl font-bold text-[#0F1649] mb-4">
            {lpConfig.pricing.heading}
          </h2>
          <p className="text-gray-500 mb-8">{lpConfig.pricing.subtitle}</p>

          {/* Toggle */}
          <div className="inline-flex bg-gray-100 rounded-full p-1">
            <button
              type="button"
              className={cn(
                "px-4 py-2 rounded-full text-sm transition-all",
                !isAnnual
                  ? "bg-white shadow-sm font-bold text-[#0F1649]"
                  : "text-gray-500",
              )}
              onClick={() => setIsAnnual(false)}
            >
              {lpConfig.pricing.monthlyLabel}
            </button>
            <button
              type="button"
              className={cn(
                "px-4 py-2 rounded-full text-sm transition-all",
                isAnnual
                  ? "bg-white shadow-sm font-bold text-[#0F1649]"
                  : "text-gray-500",
              )}
              onClick={() => setIsAnnual(true)}
            >
              {lpConfig.pricing.annualLabel}
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {lpConfig.pricing.plans.map(plan => {
            const displayPrice = plan.monthlyPrice
              ? isAnnual
                ? Math.round(
                    plan.monthlyPrice * (1 - lpConfig.pricing.annualDiscount),
                  )
                : plan.monthlyPrice
              : null;

            return (
              <div
                key={plan.name}
                className={cn(
                  "p-8 rounded-2xl border relative",
                  plan.isPopular
                    ? "border-[#ff8d00] shadow-xl scale-105"
                    : "border-gray-200",
                )}
              >
                {plan.isPopular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-block px-3 py-1 text-xs font-bold rounded-full bg-[#ff8d00] text-white">
                    {lpConfig.pricing.popularBadge}
                  </span>
                )}

                <h3 className="text-xl font-bold text-[#0F1649] mb-2">
                  {plan.name}
                </h3>
                <p className="text-sm text-gray-500 mb-4">{plan.description}</p>

                <div className="mb-6">
                  {displayPrice !== null ? (
                    <p>
                      <span className="text-4xl font-bold text-[#02072F]">
                        {formatPrice(displayPrice)}
                      </span>
                      <span className="text-base text-gray-400 font-normal">
                        {lpConfig.pricing.priceSuffix}
                      </span>
                    </p>
                  ) : (
                    <p className="text-4xl font-bold text-[#02072F]">
                      {plan.priceLabel}
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map(feature => (
                    <li key={feature} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#30BF78] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  className={cn(
                    "w-full py-3 rounded-xl font-bold text-center mt-6 transition-colors",
                    plan.isPopular
                      ? "bg-[#ff8d00] text-white hover:opacity-90"
                      : "bg-gray-100 text-[#0F1649] hover:bg-gray-200",
                  )}
                >
                  {plan.monthlyPrice
                    ? lpConfig.pricing.ctaWithPrice
                    : lpConfig.pricing.ctaWithoutPrice}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
